import * as Yup from "yup";
import Form from "react-bootstrap/Form";
import Select from "react-select";
import "./ManageSensor.scss";
import "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormikHelpers, useFormik } from "formik";
import { useAppSelector } from "../../../redux/hooks";
import Loading from "../../../components/LoadingPage/Loading";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import { currentSensor } from "../../../redux/sensor/sensorSlice";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ReactSVG } from "react-svg";
import editicon from "../../../assets/images/edit-icon.svg";
import starticon from "../../../assets/images/play.svg";
import stopicon from "../../../assets/images/stop-circle.svg";
import questionMarkIcon from "../../../assets/images/question-mark.svg";
import refreshIcon from "../../../assets/images/refresh-icon.svg";
import { useNavigate } from "react-router-dom";
import useSensor from "../../../Hooks/useSensor";
import GraphModel from "../../../components/graphModel/graphModel";
import { toggleClass } from "../../../utils/commonUtils";
import areaChartIcon from "../../../assets/images/area_chart_icon.svg";
import reportIcon from "../../../assets/images/report-icon.svg";
import { toast } from "react-toastify";
import BatteryDisplay from "../../../components/BatteryDisplay/BatteryDisplay";
import { TemperatureLogReportModel } from "./TemperatureLogReportModel";
import useRefrigerators from "../../../Hooks/useRefrigerators";
import thermometerIcon from "../../../assets/images/thermometer-icon.svg";
import InventoryProfileHeader from "../../../components/inventoryprofileheader/InventoryProfileHeader";
import TemperatureDisplayDialog from "../../../components/temperatureDisplay/TemperatureDisplayDialog";

const tempUnit = [
  { label: "Celsius (°C)", value: "true" },
  { label: "Fahrenheit (°F)", value: "false" },
];

const ManageSensor = () => {
  const [isUseFor, setIsUseFor] = useState<string>("view");
  const [zebraStatus, setZebraStatus] = useState<any>(null);
  const [showGraphModel, setShowGraphModel] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isTempDisplayOpen, setIsTempDisplayOpen] = useState<boolean>(false);
  const [refrigeratorTypeList, setRefrigeratorTypeList] = useState<any[]>([]);

  const navigate = useNavigate();
  const {
    getRetrieveFromMonnitAPI,
    getRetrieveFromMonnitLoading,
    startSensorAPI,
    startSensorLoading,
    stopSensorAPI,
    stopSensorLoading,
  } = useSensor();
  const { editRefrigeratorAPI, editRefrigeratorLoading, getRefrigeratorTypesAPI, getRefrigeratorTypesLoading } = useRefrigerators();
  const currentUserId = useAppSelector(selectCurrentUserId);
  const selectedRefrigerator = useAppSelector(currentSensor);
  const { sensor, ...refrigeratorDetails } = selectedRefrigerator;

  const initialValues = useMemo(() => ({
    sensorData: {
      isActive: sensor?.isActive || true,
      sensorName: sensor?.sensorName || "",
      sensorId: sensor?.sensorId || null,
      securityCode: sensor?.securityCode || "",
      clientCode: sensor?.clientCode || "",
      enrolled: sensor?.enrolled || false,
      lowerTempLimit: sensor?.lowerTempLimit ?? null,
      higherTempLimit: sensor?.higherTempLimit ?? null,
      intervalMinutes: sensor?.intervalMinutes ?? null,
      intervalSeconds: sensor?.intervalSeconds ?? null,
      taskId: sensor?.taskId || null,
      zebraId: sensor?.zebraId || null,
      alarmActive: sensor?.alarmActive || false,
      inCentigrade: sensor?.inCentigrade?.toString(),
      sensorMACAddress: sensor?.sensorMACAddress || null,
    },
    refrigeratorName: refrigeratorDetails?.refrigeratorName || null,
    contactNo: refrigeratorDetails?.contactNo || null,
    email: refrigeratorDetails?.email || null,
    placement: refrigeratorDetails?.placement || null,
    refrigeratorTypeId: refrigeratorDetails?.refrigeratorTypeId || null,
    alarmActive: sensor?.alarmActive || false
  }), [selectedRefrigerator]);

  const {
    values,
    touched,
    errors,
    setFieldValue,
    handleChange,
    handleSubmit,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      refrigeratorName: Yup.string().required("Cooler Name is required"),
      refrigeratorTypeId: Yup.string().required("Cooler Type is required"),
    }),
    onSubmit: async (values: any, { setSubmitting }: FormikHelpers<any>) => {
      if (isUseFor !== "edit") return;
      const { sensorData, ...rest } = values;
      const payload = {
        ...rest,
        clientCode: refrigeratorDetails?.clientCode,
        description: refrigeratorDetails?.description,
        isActive: refrigeratorDetails?.isActive,
        updatedBy: currentUserId,
        id: refrigeratorDetails?.id,
        sensorId: sensorData?.sensorId || 0,
        securityCode: sensorData?.securityCode || "",
      }
      try {
        const res = await editRefrigeratorAPI(payload);
        if (res) {
          toast.success("Refrigerator updated successfully");
          setIsUseFor("view");
        }
      } catch (error) {
        toast.error("Failed to update refrigerator");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    getRefrigeratorTypeList();
  }, []);

  useEffect(() => {
    getRetrieveFromZebraData(sensor?.id);
  }, [values?.sensorData?.sensorId, sensor?.id]);

  const getRefrigeratorTypeList = async () => {
    try {
      const res = await getRefrigeratorTypesAPI({});
      const list = res?.map((x: any) => ({ value: x.id, label: x.name }));
      setRefrigeratorTypeList(list || []);
    } catch (err) {
      console.error("Error while fetching refrigerator types", err);
    }
  }

  const handleStartSensor = async (sensorId: string) => {
    try {
      const res = await startSensorAPI({ id: sensorId });
      if (res?.message) {
        toast.success(res.message);
      }
      setIsUseFor("view");
    } catch (err) {
      console.error("Error starting sensor:", err);
      return err;
    } finally {
      getRetrieveFromZebraData(sensorId);
    }
  };

  const handleStopSensor = async (sensorId: string) => {
    try {
      const res = await stopSensorAPI({ id: sensorId });
      if (res?.message) {
        toast.success(res.message);
      }
      setIsUseFor("view");
    } catch (err) {
      console.error("Error stopping sensor:", err);
      return err;
    } finally {
      getRetrieveFromZebraData(sensorId);
    }
  };

  const getRetrieveFromZebraData = useCallback((sensorId: string | null) => {
    if (!values?.sensorData?.sensorId) return;

    getRetrieveFromMonnitAPI({ id: sensorId }).then((res: any[]) => {
      console.log("Retrieve from Monnit API response:", res);
      setZebraStatus(res || null);
    });
  }, [values?.sensorData?.sensorId]);

  const onCloseHandler = () => {
    navigate("/temperature-sensor");
  };

  const handletoggleGraphModal = () => {
    toggleClass();
    setShowGraphModel(!showGraphModel);
  };

  const handleToggleTempDisplayModal = () => {
    setIsTempDisplayOpen(prev => !prev);
  };

  const onShowReportModal = () => {
    toggleClass();
    setShowReportModal(true);
  };

  const onCloseReportModal = () => {
    toggleClass();
    setShowReportModal(false);
  };

  const handleRefreshTemp = () => {
    getRetrieveFromZebraData(sensor?.id ?? null);
  };

  const currentTempDetails = useMemo(() => {
    if (zebraStatus?.sensor && Object.keys(zebraStatus?.sensor).length > 0) {
      const data = zebraStatus?.sensor;
      return {
        temp: data?.lastUpdatedTemperature,
        date: data?.lastUpdatedTime,
        sensorStatus: data.sensorStatus,
        sensorStatusColor: data.sensorStatusColor,
        batteryLevel: data.batteryLevel
      };
    }
  }, [zebraStatus?.sensor]);

  const isLoading = useMemo(() => {
    return startSensorLoading || stopSensorLoading || getRetrieveFromMonnitLoading || editRefrigeratorLoading || getRefrigeratorTypesLoading;
  }, [startSensorLoading, stopSensorLoading, getRetrieveFromMonnitLoading, editRefrigeratorLoading, getRefrigeratorTypesLoading]);

  return (
    <>
      <div className="manage-sensor-wrapper">
        {isLoading && (<Loading />)}
        <div className="section-header">
          <InventoryProfileHeader
            status={refrigeratorDetails?.isActive}
            itemName={refrigeratorDetails?.refrigeratorName || values.refrigeratorName}
          />
          <div className="inventory-actions-box">
            <button className="invt-action-btn invt-edit" onClick={() => setIsUseFor("edit")}>
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>Edit Sensor</Tooltip>}
                trigger={["hover", "focus"]}
              >
                <ReactSVG className="svg-box" wrapper="span" src={editicon} />
              </OverlayTrigger>
            </button>
            {(sensor && sensor?.id) && (
              <>
                {/* {sensor && sensor?.taskId == null ? (
                  <button className="invt-action-btn invt-start" onClick={() => handleStartSensor(sensor?.id || "")}>
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 400 }}
                      overlay={<Tooltip>Start Sensor</Tooltip>}
                      trigger={["hover", "focus"]}
                    >
                      <ReactSVG
                        className="svg-box"
                        wrapper="span"
                        src={starticon}
                      />
                    </OverlayTrigger>
                  </button>
                ) : (
                  <button className="invt-action-btn invt-stop" onClick={() => handleStopSensor(sensor?.id || "")}>
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 400 }}
                      overlay={<Tooltip>Stop Sensor</Tooltip>}
                      trigger={["hover", "focus"]}
                    >
                      <ReactSVG className="svg-box" wrapper="span" src={stopicon} />
                    </OverlayTrigger>
                  </button>
                )} */}
                <button className="invt-action-btn invt-temp-display" onClick={handleToggleTempDisplayModal}>
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    overlay={<Tooltip>Temp. Display</Tooltip>}
                    trigger={["hover", "focus"]}
                  >
                    <ReactSVG
                      className="svg-box"
                      wrapper="span"
                      src={thermometerIcon}
                    />
                  </OverlayTrigger>
                </button>
              </>
            )}
            <button className="invt-action-btn invt-graph" onClick={handletoggleGraphModal} >
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>Temp. Graph</Tooltip>}
                trigger={["hover", "focus"]}
              >
                <ReactSVG
                  className="svg-box"
                  wrapper="span"
                  src={areaChartIcon}
                />
              </OverlayTrigger>
            </button>
            <button className="invt-action-btn invt-report" onClick={onShowReportModal}>
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>Temp. Report</Tooltip>}
                trigger={["hover", "focus"]}
              >
                <ReactSVG
                  className="svg-box"
                  wrapper="span"
                  src={reportIcon}
                />
              </OverlayTrigger>
            </button>
            {currentTempDetails && (
              <span className="invt-action-btn invt-battery">
                <BatteryDisplay level={currentTempDetails?.batteryLevel ?? 0} />
              </span>
            )}
          </div>
        </div>
        <Form onSubmit={handleSubmit} className="manage-sensor-form">
          <div className="details-box">
            <div className="form-fields-grid">
              <Form.Group className="form-field">
                <Form.Label>Sensor Id</Form.Label>
                <Form.Control
                  id="sensorId"
                  name="sensorId"
                  type="number"
                  placeholder="Sensor Id"
                  disabled={true}
                  value={values.sensorData.sensorId}
                />
              </Form.Group>
              {/* <Form.Group className="form-field">
                <Form.Label>Sensor Code</Form.Label>
                <Form.Control
                  id="securityCode"
                  name="securityCode"
                  type="text"
                  placeholder="Security Code"
                  disabled={true}
                  value={values.sensorData.securityCode}
                />
              </Form.Group> */}
              <Form.Group className="form-field">
                <Form.Label>Active </Form.Label>
                <Form.Check
                  type="switch"
                  id="isActive"
                  name="isActive"
                  onChange={handleChange}
                  disabled={true}
                  checked={values.sensorData.isActive}
                />
              </Form.Group>
              {currentTempDetails && (
                <Form.Group style={{ textAlign: "right" }}>
                  <h3>{currentTempDetails.temp}</h3>
                  <p style={{ margin: 0, lineHeight: "1.5" }}>
                    {currentTempDetails.date}{" "}
                    <span
                      className="invt-action-btn envt-edit-btn"
                      onClick={() => handleRefreshTemp()}
                    >
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<Tooltip>Refresh Temperature</Tooltip>}
                        trigger={["hover", "focus"]}
                      >
                        <ReactSVG
                          className="svg-box"
                          wrapper="span"
                          src={refreshIcon}
                        />
                      </OverlayTrigger>
                    </span>
                  </p>
                  <p style={{ margin: 0 }}>
                    Status:{" "}
                    <span
                      style={{
                        fontWeight: "bold",
                        color: currentTempDetails?.sensorStatusColor
                          ? currentTempDetails.sensorStatusColor.toLowerCase()
                          : undefined,
                      }}
                    >
                      {currentTempDetails?.sensorStatus}
                    </span>
                  </p>
                </Form.Group>
              )}
            </div>
          </div>

          <div className="details-box">
            <p className="form-section-title">Cooler Details</p>
            <div className="form-fields-grid">
              <Form.Group className="form-field">
                <Form.Label>Cooler Name</Form.Label>
                <Form.Control
                  id="refrigeratorName"
                  name="refrigeratorName"
                  type="text"
                  placeholder="Cooler Name"
                  onChange={handleChange}
                  value={values?.refrigeratorName ?? ""}
                  disabled={isUseFor === "view"}
                  isInvalid={touched?.refrigeratorName && !!errors?.refrigeratorName}
                />
                {errors.refrigeratorName && touched.refrigeratorName && (
                  <Form.Control.Feedback type="invalid">
                    {errors.refrigeratorName as any}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group className="form-field">
                <Form.Label>Cooler Location</Form.Label>
                <Form.Control
                  type="text"
                  id="placement"
                  name="placement"
                  placeholder="Cooler Location"
                  onChange={handleChange}
                  value={values?.placement ?? ""}
                  disabled={isUseFor === "view"}
                  isInvalid={touched?.placement && !!errors?.placement}
                />
              </Form.Group>
              <Form.Group className="form-field">
                <Form.Label>Cooler Type</Form.Label>
                <Select
                  classNamePrefix="react-select"
                  options={refrigeratorTypeList}
                  value={refrigeratorTypeList.find(x => x.value === values.refrigeratorTypeId)}
                  onChange={(option: any) => {
                    setFieldValue("refrigeratorTypeId", option.value);
                  }}
                  className={`${touched.refrigeratorTypeId && errors.refrigeratorTypeId ? "is-invalid-border" : ""} react-select-container`}
                  placeholder="Enter Cooler Type"
                  isDisabled={true}
                />
                {errors.refrigeratorTypeId && touched.refrigeratorTypeId && (
                  <Form.Control.Feedback type="invalid">
                    {errors.refrigeratorTypeId as any}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </div>
          </div>

          <div className="details-box">
            <p className="form-section-title">Configrations</p>
            <div className="form-fields-grid">
              <Form.Group className="form-field">
                <Form.Label>Sensor Name</Form.Label>
                <Form.Control
                  id="sensorName"
                  name="sensorName"
                  type="text"
                  placeholder="Sensor Name"
                  disabled={true}
                  value={values.sensorData.sensorName}
                />
              </Form.Group>
              <Form.Group className="form-field">
                <Form.Label>Lower Temp Limit ({values.sensorData?.inCentigrade ? "°C" : "°F"})</Form.Label>
                <Form.Control
                  type="number"
                  id="lowerTempLimit"
                  name="lowerTempLimit"
                  placeholder="Lower Temp Limit"
                  disabled={true}
                  value={values?.sensorData?.lowerTempLimit ?? ""}
                />
              </Form.Group>
              <Form.Group className="form-field">
                <Form.Label>High Temp Limit ({values.sensorData?.inCentigrade ? "°C" : "°F"})</Form.Label>
                <Form.Control
                  type="number"
                  id="higherTempLimit"
                  name="higherTempLimit"
                  placeholder="Higher Temp Limit"
                  disabled={true}
                  value={values?.sensorData?.higherTempLimit ?? ""}
                />
              </Form.Group>
              <Form.Group className="form-field">
                <Form.Label>Interval Minutes</Form.Label>
                <Form.Control
                  type="number"
                  id="intervalMinutes"
                  name="intervalMinutes"
                  placeholder="Interval Minutes"
                  disabled={true}
                  value={values?.sensorData?.intervalMinutes ?? ""}
                />
              </Form.Group>
              <Form.Group className="form-field">
                <Form.Label>Interval Seconds</Form.Label>
                <Form.Control
                  type="number"
                  id="intervalSeconds"
                  name="intervalSeconds"
                  placeholder="Interval Seconds"
                  disabled={true}
                  value={values?.sensorData?.intervalSeconds ?? ""}
                />
              </Form.Group>
              <Form.Group className="form-field">
                <Form.Label>Temp Unit</Form.Label>
                <Form.Control
                  as="select"
                  id="inCentigrade"
                  name="inCentigrade"
                  aria-label="inCentigrade"
                  disabled={true}
                  value={values.sensorData?.inCentigrade}
                >
                  <option>Select Unit</option>
                  {tempUnit.map((role: any) => (
                    <option value={role.value} key={role.value}>
                      {role.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group className="form-field multiple">
                <Form.Group>
                  <Form.Label>Alarm </Form.Label>
                  <Form.Check
                    type="switch"
                    id="alarmActive"
                    name="alarmActive"
                    disabled={true}
                    checked={values.sensorData?.alarmActive}
                  />
                </Form.Group>
              </Form.Group>
            </div>
          </div>
          <div className="details-box">
            <p className="form-section-title">Alarm Configrations</p>
            <div className="form-fields-grid">
              <Form.Group className="form-field">
                <Form.Label>
                  Alarm Phone Number{" "}
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                      <Tooltip>
                        Please enter multiple numbers separated by commas
                      </Tooltip>
                    }
                    trigger={["hover", "focus"]}
                  >
                    <ReactSVG
                      className="svg-box"
                      wrapper="span"
                      src={questionMarkIcon}
                    />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  id="contactNo"
                  name="contactNo"
                  type="text"
                  placeholder="Enter Phone number"
                  onChange={handleChange}
                  value={values?.contactNo ?? ""}
                  disabled={isUseFor === "view" || !values?.sensorData?.alarmActive}
                />
              </Form.Group>
              <Form.Group className="form-field">
                <Form.Label>
                  Alarm Email ID{" "}
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                      <Tooltip>
                        Please enter multiple email ids separated by commas
                      </Tooltip>
                    }
                    trigger={["hover", "focus"]}
                  >
                    <ReactSVG
                      className="svg-box"
                      wrapper="span"
                      src={questionMarkIcon}
                    />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  id="email"
                  name="email"
                  type="text"
                  placeholder="Enter Email ID"
                  onChange={handleChange}
                  value={values.email ?? ""}
                  disabled={isUseFor === "view" || !values?.sensorData?.alarmActive}
                />
              </Form.Group>
            </div>
          </div>

          <div className="form-actions">
            <Button variant="outline-primary" onClick={onCloseHandler}>
              Close
            </Button>
            {isUseFor === "edit" && (
              <Button
                variant="primary"
                type="submit"
                onClick={handleSubmit as any}
              >
                Update
              </Button>
            )}
          </div>
        </Form >
      </div>

      {showGraphModel && (
        <GraphModel
          showModal={showGraphModel}
          handleClose={handletoggleGraphModal}
          modelName={`Temperature Log`}
          sensorData={
            (refrigeratorDetails || sensor)
              ? {
                id: sensor?.id || "",
                inCentigrade: Boolean(sensor?.inCentigrade),
                refId: refrigeratorDetails?.id || "",
              }
              : null
          }
        />
      )}

      {showReportModal && (
        <TemperatureLogReportModel
          showReportModal={showReportModal}
          onCloseReportModal={onCloseReportModal}
          selectedSensor={
            (sensor || refrigeratorDetails)
              ? {
                id: sensor?.id || "",
                serialNumber: sensor?.serialNumber || "",
                sensorName: sensor?.sensorName || "",
                inCentigrade: Boolean(sensor?.inCentigrade),
                lowerTempLimit: sensor?.lowerTempLimit,
                higherTempLimit: sensor?.higherTempLimit,
                refInfo: refrigeratorDetails,
              }
              : null
          }
        />
      )}

      {
        isTempDisplayOpen && (
          <TemperatureDisplayDialog
            sensorId={sensor?.id || ""}
            isOpen={isTempDisplayOpen}
            handleClose={handleToggleTempDisplayModal}
          />
        )
      }
    </>
  );
};

export default ManageSensor;
